import type { PacketSourceResolved } from "../../lib/api";
import { Paperclip, Mail } from "lucide-react";

/** Q42: outlook-mail packet renderer.
 *
 *  Shows the email with its real layout: from/to/subject/date in
 *  a Mail-style header, then the sanitized HTML body, then any
 *  attachments. Outlook's bridge snapshots + restores UnRead for
 *  every fetch so opening this drawer does NOT flip the read flag
 *  in Outlook. */

export function OutlookEmailView({ resolved }: { resolved: PacketSourceResolved }) {
  const { header, body, attachments } = resolved.render;
  return (
    <div className="px-4 sm:px-6 py-4 space-y-4">
      <section className="space-y-1">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {header.subject || "(no subject)"}
        </h1>
        <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          {header.when ? new Date(header.when).toLocaleString() : ""}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 px-3 py-2 text-xs space-y-0.5">
        {header.from && (
          <Row label="From" value={header.fromAddress ? `${header.from} <${header.fromAddress}>` : header.from} />
        )}
        {header.to && <Row label="To" value={header.to} />}
        {header.cc && <Row label="Cc" value={header.cc} />}
        {header.bcc && <Row label="Bcc" value={header.bcc} />}
      </section>

      {body.html ? (
        <article
          className="email-body prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-100"
          dangerouslySetInnerHTML={{ __html: body.html }}
        />
      ) : body.text ? (
        <pre className="whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-100 font-sans">
          {body.text}
        </pre>
      ) : (
        <p className="text-sm text-slate-500 italic">
          (Body unavailable. Bridge offline; showing cached snippet only.)
          <br />
          {body.snippet}
        </p>
      )}

      {attachments.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            Attachments
          </h3>
          <ul className="space-y-1">
            {attachments.map((a, i) => (
              <li
                key={`${a.filename}-${i}`}
                className="text-xs flex items-center gap-2 px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-700"
              >
                <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                <span className="font-medium truncate">{a.filename}</span>
                {typeof a.sizeBytes === "number" && (
                  <span className="text-slate-400 ml-auto">{formatBytes(a.sizeBytes)}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-slate-700 dark:text-slate-200">
      <span className="font-semibold w-10 text-slate-500 dark:text-slate-400">{label}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
